// src/utils/lossRunResolutions.js
//
// "Handled, and why" — FIG_LossRunResolutions. The color answers only "is the
// paper in my vault?" and resolutions never change it. A resolution records,
// per renewal season, that a set of policies is Satisfied / Not required /
// Still open, why, who said so, when, and the evidence, so the decision can
// be defended a year later. Rows are only ever added; a correction is a newer
// row. Pure module.

import { dateOnlyOf, daysBetween, parsePolicyIds } from './lossRunStatus';
import { needForLob } from './lossRunGrid';
import { displayDate } from './buildHandoffModel';

export const RESOLUTION_STATUSES = ['Satisfied', 'Not required', 'Still open'];
export const RESOLUTION_REASONS = ['Loss run provided', 'No known loss letter accepted', 'Underwriter did not require', 'Same carrier renewing', 'Still chasing', 'Other'];
export const HANDLED_STATUSES = new Set(['Satisfied', 'Not required']);
export const LOSS_RUN_RESOLUTIONS_LIST = 'FIG_LossRunResolutions';

const blank = v => v === null || v === undefined || String(v).trim() === '';
const isCurrent = p => ['active', 'bound'].includes(String(p?.Status ?? '').trim().toLowerCase());

// Current season = the year of the earliest ExpirationDate among the
// insured's Active/Bound policies that is today or later. null when none.
export function currentSeason(policies, today) {
  const todayKey = dateOnlyOf(today) || dateOnlyOf(new Date());
  const dates = (policies || []).filter(isCurrent).map(p => dateOnlyOf(p.ExpirationDate)).filter(d => d && d >= todayKey).sort();
  return dates.length ? dates[0].slice(0, 4) : null;
}

export const isHandled = res => !!res && HANDLED_STATUSES.has(String(res.Status ?? '').trim());
export const isStillOpen = res => !!res && String(res.Status ?? '').trim() === 'Still open';

const seasonOf = res => String(res?.RenewalYear ?? '').trim();

// Newest DecidedDate first; unknown dates last; then higher id.
export function sortResolutionsNewestFirst(resolutions) {
  return [...(resolutions || [])].sort((a, b) => {
    const da = dateOnlyOf(a.DecidedDate), db = dateOnlyOf(b.DecidedDate);
    if (da && db && da !== db) return da < db ? 1 : -1;
    if (da && !db) return -1;
    if (!da && db) return 1;
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  });
}

// Resolutions of the current season (ACTIVE) and of older seasons (HISTORY).
export function splitResolutions(resolutions, season) {
  const active = [], history = [];
  for (const r of resolutions || []) (season && seasonOf(r) === String(season) ? active : history).push(r);
  return { active: sortResolutionsNewestFirst(active), history: sortResolutionsNewestFirst(history) };
}

export const resolutionCoversPolicy = (res, policy) => parsePolicyIds(res?.PolicyIds).includes(String(policy?.id));

// The ACTIVE resolution for a policy: current season, PolicyIds contains it,
// newest DecidedDate wins. null when none.
export function resolutionForPolicy(policy, resolutions, season) {
  if (!season) return null;
  const { active } = splitResolutions(resolutions, season);
  return active.find(r => resolutionCoversPolicy(r, policy)) || null;
}

// Per-policy lookup for the grid and the to-do list (keeps lossRunGrid free of
// a dependency on this module): Map(policyId -> { resolution, handled, stillOpen, sentence })
export function resolutionIndex(policies, resolutions, season, policiesById) {
  const byId = policiesById || new Map((policies || []).map(p => [String(p.id), p]));
  const out = new Map();
  for (const p of policies || []) {
    const res = resolutionForPolicy(p, resolutions, season);
    if (!res) continue;
    out.set(String(p.id), { resolution: res, handled: isHandled(res), stillOpen: isStillOpen(res), sentence: resolutionSentence(res, byId) });
  }
  return out;
}

// OPEN: on a required / letter-accepted line, not green, and not handled.
// "Still open" resolutions keep the policy open (their note is carried).
export function isPolicyOpen({ policy, color, resolution }) {
  const need = needForLob(policy?.LOB);
  if (need !== 'required' && need !== 'letter') return false;
  if (color === 'green') return false;
  if (resolution && isHandled(resolution)) return false;
  return true;
}

/**
 * openSummary({ policies, states, resolutions, season })
 *   states: Map(policyId -> { color }) from lossRunStatus.policyState
 * → { season, open, handled, openPolicies, handledPolicies, stillOpen: [{ policy, resolution }] }
 */
export function openSummary({ policies = [], states, resolutions = [], season } = {}) {
  const openPolicies = [], handledPolicies = [], stillOpen = [];
  for (const p of policies) {
    const color = states?.get?.(String(p.id))?.color;
    const res = resolutionForPolicy(p, resolutions, season);
    const need = needForLob(p.LOB);
    const relevant = (need === 'required' || need === 'letter') && color !== 'green';
    if (!relevant) continue;
    if (res && isHandled(res)) handledPolicies.push(p);
    else {
      openPolicies.push(p);
      if (res && isStillOpen(res)) stillOpen.push({ policy: p, resolution: res });
    }
  }
  return { season, open: openPolicies.length, handled: handledPolicies.length, openPolicies, handledPolicies, stillOpen };
}

// One sentence per resolution:
// "<lines/terms>: <Status>, <Reason>, per <DecidedBy>, <MM/DD/YYYY>. Evidence: <Evidence>."
export function resolutionSentence(res, policiesById) {
  const ids = parsePolicyIds(res?.PolicyIds);
  const pols = ids.map(id => policiesById?.get?.(id)).filter(Boolean);
  const lines = [...new Set(pols.map(p => String(p.LOB ?? '').trim()).filter(Boolean))].sort();
  const terms = [...new Set(pols.map(p => { const d = dateOnlyOf(p.EffectiveDate); return d ? `${d.slice(0, 4)}-${String(Number(d.slice(0, 4)) + 1).slice(-2)}` : null; }).filter(Boolean))].sort();
  const scope = [lines.join(', ') || `${ids.length} polic${ids.length === 1 ? 'y' : 'ies'}`, terms.join(', ')].filter(Boolean).join(' / ');
  const when = displayDate(res?.DecidedDate) || 'date not on record';
  const head = `${scope}: ${blank(res?.Status) ? 'Status not on record' : String(res.Status).trim()}, ${blank(res?.Reason) ? 'reason not on record' : String(res.Reason).trim()}, per ${blank(res?.DecidedBy) ? 'decider not on record' : String(res.DecidedBy).trim()}, ${when}.`;
  const evidence = blank(res?.Evidence) ? '' : ` Evidence: ${String(res.Evidence).trim()}.`;
  const notes = isStillOpen(res) && !blank(res?.Notes) ? ` Note: ${String(res.Notes).trim()}` : '';
  return `${head}${evidence}${notes}`;
}

// ── Form ─────────────────────────────────────────────────────────────────────
export function emptyResolutionForm({ season, policyIds = [] } = {}) {
  return { RenewalYear: season ? String(season) : '', PolicyIds: policyIds.join(','), Status: '', Reason: '', DecidedBy: '', DecidedDate: '', Evidence: '', Notes: '' };
}

// Satisfied / Not required need Reason, DecidedBy, DecidedDate and Evidence;
// DecidedDate not in the future; Reason "Other" needs Notes; Still open needs
// Reason and Notes only. PolicyIds and RenewalYear are always required.
export function validateResolutionForm(form, today) {
  const e = {};
  const f = form || {};
  if (parsePolicyIds(f.PolicyIds).length === 0) e.PolicyIds = 'Pick at least one policy.';
  if (blank(f.RenewalYear) || !/^\d{4}$/.test(String(f.RenewalYear).trim())) e.RenewalYear = 'Enter the renewal year as four digits.';
  const status = String(f.Status ?? '').trim();
  if (!RESOLUTION_STATUSES.includes(status)) e.Status = 'Pick a status.';
  const reason = String(f.Reason ?? '').trim();
  if (!RESOLUTION_REASONS.includes(reason)) e.Reason = 'Pick a reason.';
  if (HANDLED_STATUSES.has(status)) {
    if (blank(f.DecidedBy)) e.DecidedBy = 'Say who decided (name and company).';
    if (blank(f.DecidedDate)) e.DecidedDate = 'Enter the date it was decided.';
    if (blank(f.Evidence)) e.Evidence = 'Point at the evidence (email subject and date, or a FIGVault path).';
  }
  if (!blank(f.DecidedDate)) {
    const d = dateOnlyOf(f.DecidedDate);
    if (!d) e.DecidedDate = 'Enter a date as YYYY-MM-DD.';
    else if (daysBetween(d, dateOnlyOf(today) || dateOnlyOf(new Date())) < 0) e.DecidedDate = 'Decided date is in the future.';
  }
  if (reason === 'Other' && blank(f.Notes)) e.Notes = 'Explain "Other" in the notes.';
  if (status === 'Still open' && blank(f.Notes)) e.Notes = 'Say what is still being chased.';
  return e;
}

// Always a NEW row (nothing is edited in place). Blank fields are not written.
export function resolutionFieldsToWrite({ form, insuredId, title }) {
  const out = { InsuredId: String(insuredId) };
  const f = form || {};
  for (const k of ['RenewalYear', 'PolicyIds', 'Status', 'Reason', 'DecidedBy', 'Evidence', 'Notes']) {
    if (!blank(f[k])) out[k] = String(f[k]).trim();
  }
  const d = dateOnlyOf(f.DecidedDate);
  if (d) out.DecidedDate = d;
  out.Title = title || `${out.Status || 'Resolution'} ${out.RenewalYear || ''} - ${out.Reason || ''}`.trim();
  return out;
}

export async function saveResolution({ token, form, insuredId, today, api, listName = LOSS_RUN_RESOLUTIONS_LIST, title }) {
  const errors = validateResolutionForm(form, today);
  if (Object.keys(errors).length) return { ok: false, errors };
  const fields = resolutionFieldsToWrite({ form, insuredId, title });
  const created = await api.createItem(token, listName, fields);
  return { ok: true, id: created?.id, fields };
}
